// The league surface's request shapes (ICD-S01).
//
// Views live in `views.ts` because every view lives together; these are the
// things a caller SENDS, which is the same split `auth.ts` makes.

import { z } from 'zod';
import {
  DraftMode,
  DraftRanking,
  DraftSettings,
  DraftType,
  LeagueFormatIdSchema,
  ScoringConfig,
  SportIdSchema,
  WaiverSystemAnswer,
} from '../rules/schemas';
import { ChosenBadge, TeamBadge } from '../teams/badges';
import { TeamBanner } from '../teams/banners';
import { TimeZone } from '../time/zone';

const Iso = z.string();

/**
 * An invite code as it is written down and read out.
 *
 * Eight characters, and the alphabet has no `0`, `O`, `1`, `I` **or `L`** — the
 * code gets read aloud across a table and typed by somebody who has never seen
 * it written. Parsing uppercases and trims, because it arrives from a URL, a
 * text message and a person squinting at a phone.
 *
 * `L` LEFT ON 2026-09-17 (decision A5). LEAGUE_SETUP.md promised an alphabet
 * with no `1/I/L` from the first draft and the seeder kept it; only this regex
 * and `newInviteCode` disagreed, so a minted code could contain an `L` that a
 * person reading it aloud would say as a one. Thirty-one characters, 31⁸ ≈
 * 8.5 × 10¹¹ — still a once-in-forever collision.
 */
export const InviteCode = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-HJKMNP-Z2-9]{8}$/, 'That does not look like an invite code.');
export type InviteCode = z.infer<typeof InviteCode>;

/** Two hex colors. The client resolves a pro team to these; the core never names a sport. */
export const TeamColors = z.object({
  primary: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  secondary: z.string().regex(/^#[0-9a-fA-F]{6}$/),
});
export type TeamColors = z.infer<typeof TeamColors>;

/**
 * A team name: trimmed, then two to forty.
 *
 * THE PATTERN IS THE MINIMUM, said so a JSON Schema can say it. `.min(2)` after
 * `.trim()` published as `minLength: 2` on the string AS SENT, so a generated
 * client accepted `"  a  "`, which the server trims to one letter and refuses
 * (the review of a02dfab). Two non-space characters with anything between is
 * the same rule on the trimmed string and on the sent one, and it is the
 * published schema's `pattern`. The maximum still counts the string as sent
 * there, which is only stricter than the server: send it trimmed.
 */
const TeamName = z
  .string()
  .trim()
  .regex(/\S[\s\S]*\S/, 'Two letters or more.')
  .max(40);

export const JoinRequest = z.object({
  teamName: TeamName,
  colors: TeamColors.optional(),
  /**
   * The invite an emailed link carries (PLAN-2026-10-28-by-email §3.1): joining
   * by it ends that invite, whatever address the account signs in with. It
   * confers nothing — the code does — so a token that names nothing is ignored.
   */
  invite: z.string().trim().min(16).max(100).optional(),
});
export type JoinRequest = z.infer<typeof JoinRequest>;
export type JoinInput = z.input<typeof JoinRequest>;

/**
 * Renaming or recoloring my own team, at `PUT /api/leagues/:id/teams/:teamId`
 * (SCENARIOS L2).
 *
 * THE JOIN'S RULES, NOT NEW ONES. `name` is exactly `JoinRequest.teamName` —
 * trimmed, two to forty — so a name the join accepts is a name a rename
 * accepts, and the other way round; `colors` is `TeamColors`, whichever way the
 * client arrived at them (a house pair, a pro team's). The three letters follow
 * the name as the join derives them (`teamAbbreviation`), so they are not sent.
 *
 * AT LEAST ONE OF THE TWO, because a body with neither changes nothing and is
 * a caller's mistake rather than a request. The parse says it with a sentence;
 * the JSON Schema says it as `anyOf` a required `name` or a required
 * `colors`. It said `minProperties: 1` in the first cut, which a body of
 * `{ "teamName": … }` or `{ "foo": 1 }` satisfies and the server refuses (the
 * review of a02dfab).
 */
export const UpdateTeamRequest = z
  .object({
    name: JoinRequest.shape.teamName.optional(),
    colors: TeamColors.optional(),
    /**
     * The team's badge, an icon or an emoji from the site's sets, a face its
     * manager made (`Face`, B-488, 2026-10-24), or null for its initials
     * (DIRECTION U25, PLAN-2026-10-18-families-and-newcomers §11). One not in
     * the sets, or a face with a part the site does not draw, is refused.
     * Added 2026-10-18.
     */
    badge: ChosenBadge.optional(),
    /**
     * The team's banner, from the site's catalog (`TEAM_BANNERS`), `seasons`
     * for one that follows the calendar, or null to take it off
     * (PLAN-2026-10-24-team-banners). An earned one its saver has not earned
     * is refused, unless the team has it already. Added 2026-10-24.
     */
    banner: TeamBanner.nullable().optional(),
  })
  .superRefine((body, ctx) => {
    if (
      body.name === undefined &&
      body.colors === undefined &&
      body.badge === undefined &&
      body.banner === undefined
    )
      ctx.addIssue({
        code: 'custom',
        path: ['name'],
        message: 'Send a new name, new colors, a badge, a banner, or any of them.',
      });
  })
  .meta({
    anyOf: [
      { required: ['name'] },
      { required: ['colors'] },
      { required: ['badge'] },
      { required: ['banner'] },
    ],
  });
export type UpdateTeamRequest = z.infer<typeof UpdateTeamRequest>;
export type UpdateTeamInput = z.input<typeof UpdateTeamRequest>;

/**
 * The setup wizard's answers, as they arrive at `POST /api/leagues`.
 *
 * THE ANSWERS, NOT THE RULES. The obvious alternative was to let the client
 * send a whole `LeagueSettings` — it already builds one to render the preview
 * — and store that. It is rejected on purpose: the stored rules would then be
 * whatever the caller said they were, and `LeagueSettings` is 19 sections
 * deep with a scoring table in it. The server builds the settings from these
 * answers with the same `buildDefaultSettings` the preview used, so the
 * sentence somebody read on the last step and the rules the league gets come
 * from one function.
 *
 * `teamCount` starts at 4 rather than the schema's 2, matching the wizard's
 * stepper and the "close seats" floor in LEAGUE_SETUP.md. Two people is not a
 * league, it is a bet.
 */
export const CreateLeagueRequest = z.object({
  sport: SportIdSchema,
  name: z.string().trim().min(3, 'Three letters or more, please.').max(40),
  teamCount: z.number().int().min(4, 'Four teams or more.').max(20),
  season: z.number().int().min(2020).max(2100),
  /**
   * IANA zone, from the commissioner's browser. Locks and waivers use it. One
   * this runtime can name (`TimeZone`, the audit of 2026-10-20): a zone it
   * cannot format failed every read of the league's weeks and every plan
   * the clock made for it.
   */
  timeZone: TimeZone,
  format: LeagueFormatIdSchema.optional(),
  /**
   * A head-to-head league also plays the league median each week (scenario
   * Q3). Absent is no; a league that plays the field has no second game to
   * add, and it is no whatever is sent.
   */
  medianGame: z.boolean().optional(),
  /**
   * A head-to-head league plays two opponents every regular-season week, its
   * one score against each (scenario Q6). Absent is no; a league that plays
   * the field has nobody to play twice, and it is no whatever is sent.
   * Added 2026-11-01.
   */
  twoOpponents: z.boolean().optional(),
  scoringPresetId: z.string().trim().max(60).optional(),
  /**
   * The answers to the sport's scoring questions (`SportSummary.scoringQuestions`),
   * question id to option value; each absent is its default. They choose the
   * scoring and the roster, and win over `scoringPresetId`. An unknown
   * question or answer, or one the sport cannot play yet, is refused. Added
   * 2026-10-18 (DIRECTION A12).
   */
  scoringSetup: z.record(z.string().max(40), z.string().max(40)).optional(),
  /**
   * The scoring itself, when the commissioner changed a number on the form:
   * for this one question the table is the answer (PLAN-2026-10-18 §4). It
   * replaces the scoring the answers give; it is held to the same checks as
   * a settings save, `SCORING_LIMITS` included, and a refusal names the row.
   * Added 2026-10-18.
   */
  scoring: ScoringConfig.optional(),
  /**
   * The roster, when the commissioner set it under the form's More options
   * (PLAN-2026-10-18-families-and-newcomers §4.6): each slot the sport has
   * and its count, and the injured-reserve spots. Absent is the roster the
   * scoring answers give. A slot the sport does not have, or a roster with no
   * starter, is refused; the draft's rounds follow it, a round per spot.
   * Added 2026-10-18.
   */
  roster: z
    .object({
      slots: z
        .array(
          z.object({
            slot: z.string().trim().min(1).max(12),
            count: z.number().int().min(0).max(20),
          }),
        )
        .min(1)
        .max(30),
      reserve: z.number().int().min(0).max(10),
    })
    .optional(),
  /**
   * How the picks are made: a pick order, or `auction`. `offline` is the
   * spelling before settings version 3 (BACKLOG D1) and still accepted, as a
   * snake draft entered by the commissioner (`draftMode: 'manual'`): the API
   * removes nothing (API.md §1.1).
   */
  draftType: z.enum([...DraftType.options, 'offline']).optional(),
  /** Who is present and who enters the picks. Absent is `autopick`. */
  draftMode: DraftMode.optional(),
  /** Null is "not sure yet", which is a real answer and not a missing one. */
  draftScheduledAt: z.string().datetime({ offset: true }).nullable().default(null),
  pickSeconds: DraftSettings.shape.pickSeconds.optional(),
  /**
   * The hours a live draft's clock stops, in `timeZone` (C7, B-341): asked
   * with the clock, because the draft section is set when the league is made
   * (B-197). Absent or null is a clock that never stops.
   */
  overnightPause: DraftSettings.shape.overnightPause.optional(),
  /**
   * Managers trade picks and drafted players in the room during a live draft
   * (DRAFT_ROOM.md "Trading during the draft"), asked here because the draft
   * section is set when the league is made (B-197). Absent is no; a draft not
   * held in the room has no room to trade in, and it is no whatever is sent.
   */
  pickTradingAllowed: z.boolean().optional(),
  /**
   * An auction's choices (AUCTION.md §4), read when `draftType` is
   * `auction`; each absent is its default ($200, 30 seconds to nominate, 20 to
   * bid). A bid clock of an hour or more is a slow auction, with `lotsAtOnce`
   * players up at once (3 unless sent). `leftoverToWaivers` is honored only
   * when waivers are bid on.
   */
  auction: z
    .object({
      budget: z.number().int().min(1).max(10_000).optional(),
      nominationSeconds: z
        .number()
        .int()
        .min(10)
        .max(24 * 3600)
        .optional(),
      bidSeconds: z
        .number()
        .int()
        .min(5)
        .max(24 * 3600)
        .optional(),
      lotsAtOnce: z.number().int().min(1).max(10).optional(),
      leftoverToWaivers: z.boolean().optional(),
    })
    .optional(),
  /**
   * How every manager ranks for the autopick draft: one list with position
   * limits, or a list per position with a position for every round. The
   * commissioner's choice, not each manager's (DRAFT_MODES §4.1). Absent is
   * one list.
   */
  draftRanking: DraftRanking.optional(),
  /**
   * The league's first regular-season period — "start with week 3".
   *
   * Null asks the server: the first period of the sport's regular season whose
   * first kickoff is after the draft (or after now, with no draft time), which
   * is the sport's first period before the season starts. A number is checked
   * against the same rule and refused when that week kicks off first. The
   * choices, and which of them can be picked, are
   * `GET /api/sports/:sport/calendar?season=&after=`.
   */
  firstPeriod: z.number().int().min(1).max(60).nullable().default(null),
  /**
   * How waivers work. Absent is `reverse-standings`: the worst team claims
   * first, the order following the standings. `faab` gives every team a
   * season budget to bid from. The other two systems the rules know (`none`,
   * `rolling`) are refused here — they are not questions a new league is
   * asked (B-135).
   */
  waiverSystem: WaiverSystemAnswer.optional(),
  /**
   * How many divisions (PLAN-2026-10-29-divisions §4): 0, 2, 3 or 4, named by
   * the server (`divisionsFor`) and every seat placed in one by seat. One, or
   * more than one a pair of teams, is refused. Absent is none. Added 2026-10-29.
   */
  divisions: z.number().int().min(0).max(4).optional(),
  /** The commissioner's own team colors. The team NAME is a placeholder. */
  colors: TeamColors.optional(),
});
export type CreateLeagueRequest = z.infer<typeof CreateLeagueRequest>;
export type CreateLeagueInput = z.input<typeof CreateLeagueRequest>;

/**
 * Handing the league over.
 *
 * ADDRESSED BY SEAT, NOT BY ACCOUNT. `teamId` is what a league member can see
 * — no view in this product hands out a user id (ICD-S01 §7 invariant 6) — and
 * the server resolves the seat to its owner. `keepAs` is the outgoing
 * commissioner's own choice, because stepping back and stepping down are
 * different things and only they know which one this is.
 */
export const TransferCommissionerRequest = z.object({
  teamId: z.string().trim().min(1),
  keepAs: z.enum(['manager', 'co-commissioner']),
});
export type TransferCommissionerRequest = z.infer<typeof TransferCommissionerRequest>;

/**
 * Leaving a league, and what becomes of my team (`POST /api/leagues/:id/leave`).
 *
 * `open`, the default and what an empty body means: the team stands with
 * nobody, for the commissioner to hand on. `bot`: a bot plays it from then on
 * — the way out AI_MANAGERS.md §8.2 offers a manager who has drifted away,
 * built in J3 — and the commissioner can still hand it to a person. Past the
 * draft only; before it, a bot takes every seat still open when the draft runs.
 */
export const LeaveLeagueRequest = z.object({
  team: z.enum(['open', 'bot']).default('open'),
});
export type LeaveLeagueRequest = z.infer<typeof LeaveLeagueRequest>;

/**
 * One move in an election: start it, vote in it, or count it.
 *
 * A discriminated union rather than three endpoints, because they are three
 * moves in one object — and `teamId` is only meaningful on one of them, which
 * a flat shape would let a caller send anyway.
 */
export const ElectionRequest = z.discriminatedUnion('intent', [
  z.object({ intent: z.literal('open') }),
  z.object({ intent: z.literal('vote'), teamId: z.string().trim().min(1) }),
  z.object({ intent: z.literal('resolve') }),
]);
export type ElectionRequest = z.infer<typeof ElectionRequest>;

/**
 * What an invite code names, to somebody who does not have an account yet.
 *
 * The only shape in the product readable with no session, and deliberately
 * the smallest one that does the job: it says enough to make somebody want
 * to join and nothing about who is already there. ICD-S01 §9.
 */
export const InvitePreview = z.object({
  leagueName: z.string(),
  sport: z.string(),
  season: z.number().int(),
  joined: z.number().int(),
  of: z.number().int(),
  /** Display name only. Never an id, never an address. */
  commissionerName: z.string(),
  draftAt: z.string().nullable(),
  /**
   * Whether this code seats somebody now. A league's code: false once the
   * draft has started, when a code is not enough. A team's link (`team`):
   * true until it is used or canceled.
   */
  open: z.boolean(),
  /**
   * THE TEAM A LINK HANDS OVER, when the code is one team's link rather than
   * the league's (MANAGERS.md, J4): the team as the league knows it, its
   * record so far, and who made the link — a display name, as
   * `commissionerName` is. Null for a league's code. Nothing about the
   * manager who had it. Added 2026-10-01.
   */
  team: z
    .object({
      name: z.string(),
      abbreviation: z.string(),
      colors: TeamColors,
      /** Wins, losses and ties so far; null before a week is settled. */
      record: z
        .object({ wins: z.number().int(), losses: z.number().int(), ties: z.number().int() })
        .nullable(),
      invitedBy: z.string(),
      /**
       * What the link makes the person who follows it: the team's `manager`,
       * taking it over (J4), or a `co-manager` beside its manager (B13,
       * PLAN-2026-10-18-families-and-newcomers §5). Added 2026-10-18; absent
       * reads as `manager`, the only kind there was.
       */
      as: z.enum(['manager', 'co-manager']).default('manager'),
      /** The team's badge, as `TeamRef.badge` (DIRECTION U25). Added 2026-10-18. */
      badge: TeamBadge.default(null),
    })
    .nullable()
    .default(null),
});
export type InvitePreview = z.infer<typeof InvitePreview>;

/**
 * `GET /api/leagues/:id/teams/:teamId/managers` — who runs a team: its manager
 * and its co-managers (DIRECTION A14, scenario B13). Every member of the league
 * reads it; the link is handed only to those who may send it.
 */
export const TeamManagersView = z.object({
  teamId: z.string(),
  teamName: z.string(),
  /** The manager, by name; null for a team nobody runs, which can have no co-managers. */
  manager: z.string().nullable(),
  /** Oldest first. `id` is the co-manager's own, never an account's (ICD-S01 §7 invariant 6). */
  coManagers: z.array(
    z.object({ id: z.string(), name: z.string(), addedAt: Iso, isMe: z.boolean() }),
  ),
  /** The most co-managers a team may have. */
  max: z.number().int().min(1),
  /** The reader co-manages this team, and may step down. */
  isCoManager: z.boolean(),
  /** The reader may invite and remove co-managers: the team's manager, or a commissioner. */
  canManage: z.boolean(),
  /** The live co-manager link, for those who may send it; null otherwise, or with none out. */
  invite: z.object({ code: z.string(), createdAt: Iso }).nullable(),
});
export type TeamManagersView = z.infer<typeof TeamManagersView>;

/**
 * Set one lineup. BACKLOG E2.
 *
 * Every slot the caller means to set, including the ones it means to EMPTY
 * (`playerId: null`) — a partial body would make "leave this alone" and "take
 * this player out" the same request, and the second is a thing somebody does
 * on purpose thirty seconds before kickoff.
 */
export const SaveLineupRequest = z.object({
  slots: z
    .array(
      z.object({
        slot: z.string().min(1).max(20),
        slotIndex: z.number().int().min(0).max(40),
        playerId: z.string().min(1).max(64).nullable(),
      }),
    )
    .min(1)
    .max(60),
  /** Defaults to the period happening now. */
  period: z.number().int().min(1).max(60).optional(),
});
export type SaveLineupRequest = z.infer<typeof SaveLineupRequest>;

/** A commissioner's reason for an override: their own words, required (COMMISSIONER_OVERRIDES.md). */
const OverrideReason = z.string().trim().min(1, 'Say why.').max(300);

/**
 * `PUT /api/leagues/:id/teams/:teamId/lineup/fix?period=` — a commissioner
 * fixing a team's lineup for a week that is over (J1). The slots as the save
 * takes them; the week is in the query, required.
 */
export const FixLineupRequest = z.object({
  slots: SaveLineupRequest.shape.slots,
  reason: OverrideReason,
});
export type FixLineupRequest = z.infer<typeof FixLineupRequest>;

/**
 * `POST /api/leagues/:id/matchups/:matchupId/adjustments` — a commissioner
 * adding or taking points for one team in one week (J1). Points in tenths, as
 * a score is shown; never zero.
 */
export const AdjustPointsRequest = z.object({
  teamId: z.string().min(1).max(64),
  points: z
    .number()
    .min(-999.9)
    .max(999.9)
    .refine((n) => n !== 0, 'An adjustment of nothing changes nothing.')
    .refine((n) => Math.round(n * 10) === n * 10, 'Points go to a tenth, as a score is shown.'),
  reason: OverrideReason,
});
export type AdjustPointsRequest = z.infer<typeof AdjustPointsRequest>;

/**
 * What an override did (J1): the commissioner log's sentence, the week, the
 * matchup it moved (null for a team with no game that week), and how many
 * matchup scores moved when the week was re-settled. A client reads its
 * screens again after it, as after any write.
 */
export const OverrideDone = z.object({
  sentence: z.string(),
  period: z.number().int(),
  matchupId: z.string().nullable(),
  changed: z.number().int(),
  /**
   * What could not be brought up to date after the scores moved — the week's
   * awards, a bracket round already played — said to the commissioner. The
   * change itself stands and the managers were told. Empty almost always.
   */
  problems: z.array(z.string()),
});
export type OverrideDone = z.infer<typeof OverrideDone>;
